import { sendCapRequest, parseCapResponse } from './request';
import { getCapApiConfig } from './config';
import {
  IPaymentData,
  ICheckoutDataResponse,
  IGooglePayConfig,
  IMerchantPaymentUrlResponse,
  IEstimatePaymentResponse,
} from './types';

export const payment = {
  /**
   * Retrieves checkout data including integrity token and checkout ID
   * Public endpoint - no authentication required
   * 
   * @param sessionId - Session identifier
   * @param env - Environment (dev, staging, prod)
   * @returns Checkout data for payment processing
   */
  getCheckoutData: async (sessionId: string, env?: string): Promise<ICheckoutDataResponse> => {
    const response = await sendCapRequest(
      `psp/cap/session/checkout-data?sessionId=${sessionId}`,
      'GET',
      null,
      undefined,
      {},
      env
    );
    return parseCapResponse<ICheckoutDataResponse>(response);
  },

  /**
   * Retrieves payment information by checkout ID
   * Requires session ID header
   * 
   * @param checkoutId - CAP checkout identifier
   * @param sessionId - Session identifier
   * @param env - Environment (dev, staging, prod)
   * @returns Payment details
   */
  getPaymentByCheckoutId: async (checkoutId: string, sessionId: string, env?: string): Promise<IPaymentData> => {
    const response = await sendCapRequest(
      `psp/cap/payment/${checkoutId}`,
      'GET',
      null,
      sessionId,
      {},
      env
    );
    return parseCapResponse<IPaymentData>(response);
  },

  /**
   * Retrieves Google Pay configuration for the session
   * Public endpoint - no authentication required
   * 
   * @param sessionId - Session identifier
   * @param env - Environment (dev, staging, prod)
   * @returns Google Pay configuration
   */
  getGooglePayConfig: async (sessionId: string, env?: string): Promise<IGooglePayConfig> => {
    const response = await sendCapRequest(
      `psp/cap/google-pay-config?sessionId=${sessionId}`,
      'GET',
      null,
      undefined,
      {},
      env
    );
    return parseCapResponse<IGooglePayConfig>(response);
  },

  /**
   * Generates the URL for the merchant widget
   * Requires session ID header
   * 
   * @param sessionId - Session identifier
   * @param env - Environment (dev, staging, prod)
   * @returns Merchant payment URL
   */
  generateMerchantPaymentUrl: async (sessionId: string, env?: string): Promise<IMerchantPaymentUrlResponse> => {
    const response = await sendCapRequest(
      'psp/cap/merchant-payment-url',
      'POST',
      null,
      sessionId,
      {},
      env
    );
    return parseCapResponse<IMerchantPaymentUrlResponse>(response);
  },

  /**
   * Generates widget URL for iframe embedding
   * Public endpoint - no authentication required
   * 
   * @param sessionId - Session identifier
   * @param checkoutId - Checkout identifier from session update
   * @param redirectUrl - URL to redirect after payment
   * @param theme - Site theme (dark/light)
   * @param env - Environment (dev, staging, prod)
   * @returns Widget URL
   */
  getWidgetUrl: (sessionId: string, checkoutId: string, redirectUrl: string, theme?: string, env?: string): string => {
    const { baseUrl } = getCapApiConfig(env);
    const encodedRedirectUrl = encodeURIComponent(redirectUrl);
    const themeParam = theme ? `&theme=${theme}` : '';
    return `${baseUrl}/psp/cap/widget?sessionId=${sessionId}&redirectUrl=${encodedRedirectUrl}&checkoutId=${checkoutId}${themeParam}`;
  },
  
  /**
   * Generates merchant widget URL for iframe embedding
   * Public endpoint - no authentication required
   * 
   * @param sessionId - Session identifier
   * @param env - Environment (dev, staging, prod)
   * @returns Merchant widget URL
   */
  getMerchantWidgetUrl: (sessionId: string, env?: string): string => {
    const { baseUrl } = getCapApiConfig(env);
    return `${baseUrl}/psp/cap/merchant-widget?sessionId=${sessionId}`;
  },

  /**
   * Estimates payment conversion
   * Public endpoint - no authentication required
   * 
   * @param paymentRouteId - Payment route identifier
   * @param amount - Amount to convert
   * @param env - Environment (dev, staging, prod)
   * @returns Estimate payment data
   */
  estimatePayment: async (paymentRouteId: string, amount: string, env?: string): Promise<IEstimatePaymentResponse> => {
    const response = await sendCapRequest(
      `psp/cap/estimate-payment?payment_route_id=${paymentRouteId}&amount=${amount}`,
      'GET',
      null,
      undefined,
      {},
      env
    );
    return parseCapResponse<IEstimatePaymentResponse>(response);
  },

  /**
   * Gets payment status by checkout ID
   * Requires session ID header
   * 
   * @param sessionId - Session identifier
   * @param checkoutId - CAP checkout identifier (id from callback params)
   * @param env - Environment (dev, staging, prod)
   * @returns Payment status data
   */
  getPaymentStatus: async (sessionId: string, checkoutId: string, env?: string): Promise<IPaymentData> => {
    const response = await sendCapRequest(
      `psp/cap/payment?id=${checkoutId}`,
      'GET',
      null,
      sessionId,
      {},
      env
    );
    return parseCapResponse<IPaymentData>(response);
  },
};

